import React, { useContext, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Input, Row, Table } from "reactstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import _ from "lodash";
import { ColPromptCheckBox } from "./index";
import { AttachmentManager, ConfirmModal, ItemTypeahead, ServiceThreadSpinner } from "../index";
import { constants, dateHelpers, helpers, UserContext } from "../../utils";
import { DesktopContainer } from "../../layouts";
import { ColValue } from "../ProductionModule/index";
import { useMachineSetupContextState } from "./useMachineSetupContextState";
import { useMachineSetupContextUpdater } from "./useMachineSetupContextUpdater";
import { useMachineSetupContextLoading } from "./useMachineSetupContextLoading";
import classNames from "classnames";

function ColPrompt({ title, hideColon, colorWhite = true, bold = false }) {
   return (
      <Col xxl={3} xl={3} lg={3} className={classNames("", { "text-white": colorWhite })}>
         <h5 className={classNames("text-end", { "fw-bold": bold })}>
            {title}
            {hideColon ? false : ":"}
         </h5>
      </Col>
   );
}

const { CALCULATION_TYPES } = constants;

export default function MachineSetups() {
   const { currentUser } = useContext(UserContext);
   const [copyConfirm, setCopyConfirm] = useState(null);
   const MachineSetupContextValue = useMachineSetupContextState();
   const MachineSetupContextUpdater = useMachineSetupContextUpdater();
   const MachineSetupContextLoading = useMachineSetupContextLoading();
   const {
      machineSetup,
      machineNums,
      selectedMachineSetup,
      selectedMachines,
      selectedItem,
      specification,
      copyToItemOptions,
      printOptions,
      itemToCopy,
      itemToDelete,
      machineTypes,
      MachineSetupDisplay,
      calcTypes,
   } = MachineSetupContextValue;
   const {
      handleItemSelectChange,
      onChange,
      onChangeSelectedMachine,
      setSpecification,
      newHandleNewSetup,
      handldePrintActiveSetups,
      setPrintOptions,
      handlePrintMachineSetup,
      handleDeactivateMachineSetup,
      handleDeleteMachineSetup,
      handleCopyMachineItemSetup,
      setItemToCopy,
      setItemToDelete,
      setSelectedMachineSetup,
      handleSaveMachineSetup,
   } = MachineSetupContextUpdater;
   const { isLoading } = MachineSetupContextLoading;
   const specificationExistsAndNotEmpty = specification && !_.isEmpty(specification.machineSetupList);
   const specificationExistsButEmpty = specification && _.isEmpty(specification.machineSetupList);

   function handleItemSelectToCopy(e, machineTypeProp, index, machineProperties) {
      let newList = specification.machineSetupList.slice();
      //machineSetup with the item to copy property appended
      let machineSetup = _.assign({}, newList[index]);
      machineSetup["itemToCopy"] = e;
      newList[index] = machineSetup;
      let newInfo = _.assign({}, specification);
      newInfo.machineSetupList = newList;
      if (!machineProperties.extendedPropertyData || _.isEmpty(machineProperties.extendedPropertyData)) {
         toast.error("Missing properties for copy, contact an admin..");
         return;
      }
      setSpecification(newInfo);
      setItemToCopy({
         ...e,
         machineType: machineTypeProp,
         extendedPropertyJson: machineProperties.extendedPropertyData,
         calcType: machineProperties.calcType,
      });
   }

   const showProcessStep = specification &&
      _.some(specification.machineSetupList, (ms) => {
         return (
            ms.orderToProcess > 0 &&
            ms.calcType === constants.CALCULATION_TYPES.CUMULATIVE.value
         );
      });

   const canDelete = currentUser?.isAdmin || currentUser?.isPowerUser;
   const canActivate = currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.canActivateMachineSetups;
   const canEdit = currentUser?.isAdmin || currentUser?.isPowerUser || helpers.hasRoleId(constants.ROLE_IDS.EngineeringEdit, currentUser);

   return (
      (document.title = `Machine Setups`),
      (
         <DesktopContainer screenName={"Machine Setups"}>
            <div className="filter-header mb-3">
               <Row>
                  <ColPrompt title={"Item"} colorWhite />
                  <ColValue>
                     <ItemTypeahead
                        value={MachineSetupContextValue.selectedItem}
                        onChange={(opt) => handleItemSelectChange(opt)}
                        filterItemBy={constants.TYPE_AHEAD_CLASSES.SpecItemsWithAllActiveSamples}
                        includeSampleItems={true}
                     />
                  </ColValue>
               </Row>
               <Row className={classNames("", { "mt-5": isLoading })}>
                  <Col>
                     <ServiceThreadSpinner on={isLoading} white>
                        {!!selectedItem && (
                           <>
                              <div className={classNames("filter-header p-3 text-center fs-4 text-brightRed", { "d-none": specificationExistsAndNotEmpty })}>
                                 <span>No machine setups exist for this item</span>
                              </div>
                              {_.isNil(specification) && (
                                 <div className="filter-header p-3 text-center fs-4">
                                    <span className="text-brightRed">
                                       No specification exist for this item
                                    </span>
                                 </div>
                              )}

                              {specification && (
                                 <>
                                    <Row className="mb-2">
                                       <ColPrompt title={"Quality Machine Setup"} colorWhite />
                                    </Row>
                                    <Row className="mb-2">
                                       {specificationExistsAndNotEmpty ? (false) : (
                                          <ColPrompt title={""} hideColon />
                                       )}
                                       <ColValue size={3}>
                                          <Button
                                             className={classNames("shredButton", {"float-end": specificationExistsAndNotEmpty,})}
                                             onClick={() => newHandleNewSetup()}
                                             disabled={isLoading}
                                          >
                                             Add New Setup
                                          </Button>
                                       </ColValue>
                                       {specificationExistsButEmpty ? (false) : (
                                          <>
                                             <ColValue size={3}>
                                                <Button
                                                   className="shredButton"
                                                   onClick={() => handldePrintActiveSetups()}
                                                   disabled={isLoading}
                                                >
                                                   Active Machine Setup Report
                                                </Button>
                                             </ColValue>
                                             <ColPromptCheckBox
                                                title={"Print Revision Notes?"}
                                                valuePRP={MachineSetupContextValue.printOptions.printRevisionNotes}
                                                callBack={() =>
                                                   setPrintOptions({
                                                      ...printOptions,
                                                      printRevisionNotes: !printOptions.printRevisionNotes,
                                                   })
                                                }
                                                alignPRP={"left"}
                                             />
                                             <ColPromptCheckBox
                                                title={"Print Configuration?"}
                                                valuePRP={MachineSetupContextValue.printOptions.printConfiguration}
                                                callBack={() =>
                                                   setPrintOptions({
                                                      ...printOptions,
                                                      printConfiguration: !printOptions.printConfiguration,
                                                   })
                                                }
                                                alignPRP={"left"}
                                             />
                                          </>
                                       )}
                                    </Row>
                                    {specificationExistsButEmpty ? (false) : (
                                       <Row className="mb-2">
                                          <Col xxl={{ offset: 1, size: 10 }}>
                                             <Table size="sm" className="data-table">
                                                <thead className="table-header">
                                                   <tr>
                                                      <th width="12%">
                                                         <span>Calc<br />Method</span>
                                                      </th>
                                                      {showProcessStep && (
                                                         <th style={{ width: "10%" }}>
                                                            Process Step
                                                         </th>
                                                      )}
                                                      <th width="28%">Machine Type</th>
                                                      <th width="5%" />
                                                      {canEdit ? <th width="5%" /> : false}
                                                      <th width="15%" />
                                                      <th width="15%" />
                                                      {canDelete ? <th width="8%" /> : false}
                                                      {canActivate ? <th width="12%" /> : false}
                                                   </tr>
                                                </thead>
                                                <tbody>
                                                   {_.map(
                                                      specification.machineSetupList,
                                                      (x, index) => {
                                                         return (
                                                            <tr
                                                               key={index}
                                                               className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                                                            >
                                                               <td className="data-cell">
                                                                  {x.calcTypeText}
                                                               </td>
                                                               {showProcessStep && (
                                                                  <td className="data-cell text-center">
                                                                     {x.calcType === constants.CALCULATION_TYPES.CUMULATIVE.value
                                                                        ? x.orderToProcess
                                                                        : null}
                                                                  </td>
                                                               )}
                                                               <td className="data-cell">
                                                                  {x.machineDescription}
                                                                  {x.settingsMissing && (
                                                                     <div className="text-brightRed fw-bold">
                                                                        No settings saved - use Edit to add them
                                                                     </div>
                                                                  )}
                                                               </td>
                                                               <td className="data-cell">
                                                                  <span
                                                                     onClick={() => handlePrintMachineSetup(index)}
                                                                     className="shredLink fw-bold"
                                                                  >
                                                                     Print
                                                                  </span>
                                                               </td>
                                                               {canEdit ? (
                                                                  <td className="data-cell">
                                                                     <span
                                                                        onClick={() => {
                                                                           x.label = x.machineDescription;
                                                                           x.value = x.machineType;
                                                                           x.extendedPropertyJson = x.extendedPropertyData ||
                                                                              {}; // this is a hack to get the extendedPropertyJson to be the same as the other machine setups inbetween renders
                                                                           setSelectedMachineSetup(x);
                                                                        }}
                                                                        className="shredLink fw-bold"
                                                                     >
                                                                        Edit
                                                                     </span>
                                                                  </td>
                                                               ) : (
                                                                  false
                                                               )}
                                                               <td className="data-cell">
                                                                  <Select
                                                                     onChange={(e) => handleItemSelectToCopy(e, x.machineType, index, x)}
                                                                     value={
                                                                        x.itemToCopy || {
                                                                           label: selectedItem.value,
                                                                           value: selectedItem.value,
                                                                        }
                                                                     }
                                                                     options={copyToItemOptions}
                                                                     classNamePrefix="serviceThreadSelect"
                                                                  />
                                                               </td>
                                                               <td className="data-cell">
                                                                  <span onClick={() => setCopyConfirm(true)} className="shredLink fw-bold">
                                                                     Copy
                                                                  </span>
                                                               </td>
                                                               {canDelete ? (
                                                                  <td className="data-cell">
                                                                     <span onClick={() => setItemToDelete(x)} className="shredLink fw-bold">
                                                                        Delete
                                                                     </span>
                                                                  </td>
                                                               ) : (
                                                                  false
                                                               )}
                                                               {canActivate ? (
                                                                  <td className="data-cell">
                                                                     <span onClick={() => handleDeactivateMachineSetup(x)} className="shredLink fw-bold">
                                                                        {x.isActive ? "Deactivate" : "Activate"}
                                                                     </span>
                                                                  </td>
                                                               ) : (
                                                                  false
                                                               )}
                                                            </tr>
                                                         );
                                                      }
                                                   )}
                                                </tbody>
                                             </Table>
                                          </Col>
                                       </Row>
                                    )}

                                    {selectedMachineSetup && machineSetup && machineSetup.extendedPropertyJson && (
                                       <Row className="mb-2">
                                          <Col xxl={{ offset: 1, size: 10 }}>
                                             <Card className="border-0">
                                                <CardHeader className="table-header">
                                                   <Row className="mb-2">
                                                      <ColPrompt title={"Item"} colorWhite={false} bold={true} />
                                                      <ColValue size={4}>
                                                         <Input
                                                            type="text"
                                                            readOnly
                                                            value={selectedItem?.value || ""}
                                                            disabled
                                                         />
                                                      </ColValue>
                                                   </Row>
                                                   <Row className="mb-2">
                                                      <ColPrompt title={"Selected Machine Type"} colorWhite={false} bold />
                                                      <ColValue size={4}>
                                                         <Select
                                                            onChange={(e) => {
                                                               e.reset = true;
                                                               e.machineType = e.value; //simple conversion to call one useEffect
                                                               setSelectedMachineSetup({
                                                                  ...selectedMachineSetup,
                                                                  ...e,
                                                               });
                                                            }}
                                                            value={selectedMachineSetup}
                                                            options={machineTypes}
                                                            isDisabled={selectedMachineSetup.machineType !== 4}
                                                            classNamePrefix="serviceThreadSelect"
                                                         />
                                                      </ColValue>
                                                      {selectedMachineSetup?.machineType !== 4 ? (
                                                         <ColValue size={3}>
                                                            <Select
                                                               isMulti={true}
                                                               name="validMachines"
                                                               options={machineNums}
                                                               value={selectedMachines}
                                                               onChange={(e) => onChangeSelectedMachine(e)}
                                                               classNamePrefix="serviceThreadSelect"
                                                            />
                                                         </ColValue>
                                                      ) : null}
                                                   </Row>
                                                   <Row className="mb-2">
                                                      <ColPrompt title={"Labor Calculation Type"} colorWhite={false} bold />
                                                      <ColValue size={4}>
                                                         <Select
                                                            onChange={(e) => { onChange("calcType", e.value); }}
                                                            value={
                                                               _.find(
                                                                  CALCULATION_TYPES,
                                                                  (x) => x.value === machineSetup?.calcType
                                                               ) || CALCULATION_TYPES.UNKNOWN
                                                            }
                                                            options={calcTypes}
                                                            classNamePrefix="serviceThreadSelect"
                                                         />
                                                      </ColValue>
                                                   </Row>
                                                   {selectedMachineSetup?.calcType === "C" ? (
                                                      <Row>
                                                         <ColPrompt title={"Order To Process"} colorWhite={false} bold />
                                                         <ColValue size={4}>
                                                            <Input
                                                               type="text"
                                                               onChange={(e) => { onChange("orderToProcess", e.target.value); }}
                                                               value={machineSetup?.orderToProcess}
                                                               style={{ width: "15%" }}
                                                            />
                                                         </ColValue>
                                                      </Row>
                                                   ) : null}
                                                   <Row>
                                                      <span>
                                                         &nbsp;&nbsp;&nbsp;&nbsp;NOTE: Any change
                                                         to the labor calculation type on this
                                                         setup will cause all other setups for
                                                         this item to be changed as well.
                                                      </span>
                                                   </Row>
                                                </CardHeader>
                                                <CardBody className="data-cell">
                                                   {MachineSetupDisplay[selectedMachineSetup?.machineType]}
                                                   <Row className="mt-4 mb-2">
                                                      <Col xxl={{ offset: 1, size: 10 }}>
                                                         <Button
                                                            className="shredButton float-start"
                                                            disabled={isLoading}
                                                            onClick={() => handleSaveMachineSetup()}
                                                         >
                                                            Save Machine Setup
                                                         </Button>
                                                      </Col>
                                                   </Row>
                                                   {/*revision not table*/}
                                                   <Row className="my-5">
                                                      <Col xxl={{ offset: 1, size: 10 }}>
                                                         <Card>
                                                            <CardBody>
                                                               <Row className="align-items-center mb-2">
                                                                  <Col xs={12}>
                                                                     <h5 className="float-start">Revision History:</h5>
                                                                  </Col>
                                                               </Row>
                                                               <hr />
                                                               {machineSetup.setupRevisionNotes &&
                                                                  machineSetup.setupRevisionNotes.length > 0 ? (
                                                                  <Table>
                                                                     <thead className="table-header">
                                                                        <tr>
                                                                           <th width="10%">Revision #</th>
                                                                           <th width="15%">Date</th>
                                                                           <th>Note</th>
                                                                        </tr>
                                                                     </thead>
                                                                     <tbody>
                                                                        {_.map(
                                                                           _.sortBy(
                                                                              machineSetup.setupRevisionNotes,
                                                                              (n) => { return n.revNumber; }
                                                                           ),
                                                                           (n, idx) => {
                                                                              return (
                                                                                 <tr key={`revNote${idx}${n.revNumber}`}>
                                                                                    <td className="data-cell">
                                                                                       {n.revisionNumber}
                                                                                    </td>
                                                                                    <td className="data-cell">
                                                                                       {dateHelpers.toLongDateString(n.revisionDate)}
                                                                                    </td>
                                                                                    <td className="data-cell">
                                                                                       {n.note}
                                                                                    </td>
                                                                                 </tr>
                                                                              );
                                                                           }
                                                                        )}
                                                                     </tbody>
                                                                  </Table>
                                                               ) : null}
                                                            </CardBody>
                                                         </Card>
                                                      </Col>
                                                   </Row>
                                                   {/**/}
                                                   <Row className="my-5">
                                                      <Col xxl={{ offset: 1, size: 10 }}>
                                                         <Card>
                                                            <CardBody>
                                                               <AttachmentManager
                                                                  attachmentTypeId={constants.ATTACHMENT_TYPES.MachineSetup}
                                                                  contextName={machineSetup.setupId}
                                                                  attachmentKey={`Machine Setup ${machineSetup.setupId}`}
                                                               />
                                                            </CardBody>
                                                         </Card>
                                                      </Col>
                                                   </Row>
                                                </CardBody>
                                             </Card>
                                          </Col>
                                       </Row>
                                    )}
                                 </>
                              )}
                           </>
                        )}
                     </ServiceThreadSpinner>
                  </Col>
               </Row>
            </div>

            <ConfirmModal
               confirmDelete={itemToDelete}
               approveConfirmDelete={() => { handleDeleteMachineSetup(itemToDelete); }}
               declineConfirmDelete={() => { setItemToDelete(null); }}
               disabled={isLoading}
               warningMessage={
                  <>
                     Are you sure you want to delete this machine setup?<br />
                     This will permanently remove it from the system.
                  </>
               }
            />

            <ConfirmModal
               confirmDelete={copyConfirm && itemToCopy}
               approveConfirmDelete={() => { handleCopyMachineItemSetup(); }}
               declineConfirmDelete={() => { setCopyConfirm(null); }}
               disabled={isLoading}
               warningMessage={
                  <>Are you sure you want to make a copy of this machine setup?</>
               }
            />
         </DesktopContainer>
      )
   );
}
